import { formatters } from '@Components/hierarchical-report/Layout'
import { formatDate } from 'utils'

import { getGroupbyBaseField } from './chartUtils'
import { getMetricDisplayLabel } from './collectionMetrics'

const CHART_DATE_FORMAT = 'MM/DD'

const TIME_ROW_KEYS = ['day', 'day_session', 'hour', 'hour_session', 'week', 'week_session', 'month', 'month_session', 'year']

const TIME_ROW_FORMATS = {
  hour: 'MM/DD HH:mm',
  day: 'MM/DD/YYYY',
  week: 'MM/DD/YYYY',
  month: 'MM/YYYY',
  year: 'YYYY',
}

const TIME_TICK_FORMATS = {
  hour: 'MM/DD HH:mm',
  day: CHART_DATE_FORMAT,
  week: CHART_DATE_FORMAT,
  month: 'MM/YYYY',
  year: 'YYYY',
}

function getTimeUnit(key) {
  return String(key).replace(/_session$/, '')
}

function getTimeRowFormat(key) {
  return TIME_ROW_FORMATS[getTimeUnit(key)] || CHART_DATE_FORMAT
}

const UNIT_TIME_FORMATS = {
  hour_unittime: 'HH [h]',
  day_unittime: '[Day] DD',
  weekday_unittime: 'dddd',
  month_unittime: 'MMMM',
}

function formatMoneyValue(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '--'
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function formatPercentValue(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '--'
  return `${(num * 100).toFixed(0)}%`
}

function formatCountValue(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '--'

  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1).replace('.0', '')}M`
  }

  if (num >= 1000) {
    return `${(num / 1000).toFixed(1).replace('.0', '')}k`
  }

  return num.toString()
}

function formatChartDate(value, mask = CHART_DATE_FORMAT) {
  const timestamp = parseInt(value, 10)
  if (!Number.isFinite(timestamp)) return String(value ?? '')
  return formatDate(timestamp, mask)
}

export function formatChartRowLabel(row) {
  if (!row) return ''

  const { value, name } = row
  const key = getGroupbyBaseField(row.key)

  if (TIME_ROW_KEYS.includes(key) && value != null && value !== '') {
    return formatChartDate(value, getTimeRowFormat(key))
  }

  if (UNIT_TIME_FORMATS[key] && value != null && value !== '') {
    return formatChartDate(value, UNIT_TIME_FORMATS[key])
  }

  if (row.formattedName) return row.formattedName

  if (name) return name

  const numeric = Number(value)
  if (Number.isFinite(numeric) && String(value).length >= 10) {
    return formatChartDate(numeric)
  }

  return value === '' || value == null ? '--' : String(value)
}

export function formatChartTickLabel(row) {
  if (!row) return ''

  const { value } = row
  const key = getGroupbyBaseField(row.key)
  const mask = TIME_TICK_FORMATS[getTimeUnit(key)]

  if (mask && value != null && value !== '') return formatChartDate(value, mask)

  return formatChartRowLabel(row)
}

export function formatChartSeriesLabel(row) {
  const label = formatChartRowLabel(row)
  if (!row) return label

  const { value } = row
  const key = getGroupbyBaseField(row.key)
  if (TIME_ROW_KEYS.includes(key) || UNIT_TIME_FORMATS[key]) return label
  if (value === '' || value == null) return label
  if (typeof label === 'string' && label.startsWith('[#')) return label

  return `[#${value}] - ${label}`
}

export function getChartValueFormatter(type) {
  if (type === 'money') return formatMoneyValue
  if (type === 'percentage') return formatPercentValue
  return formatCountValue
}

export function getChartAxisFormatter(type) {
  return getChartValueFormatter(type)
}

export function getMetricLegendLabel(collection, key) {
  const catalogLabel = getMetricDisplayLabel(collection, { key })
  if (catalogLabel) return catalogLabel
  return formatters.groupsText(key)
}

export function getChartLegendLabel(collection, key, keyType = 'metric') {
  if (keyType === 'metric') return getMetricLegendLabel(collection, key)
  return key
}
